import { useCallback, useState, type ReactNode } from "react";
import { can } from "@sts/schemas";
import type {
  CaptureSession,
  Me,
  MeasureCategory,
  PhotoEvidence,
  PhotoIntegrity,
  RequiredPhotosForMeasure,
  StampLine,
} from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { PhotoImage } from "../components/PhotoRail.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import {
  FACADE_LABEL,
  INTEGRITY_MARK,
  INTEGRITY_WORD,
  MEASURE_CATEGORIES,
  POSITION_IS_EVIDENCE,
  POSITION_MARK,
  POSITION_MEANING,
  POSITION_WORD,
  STAGE_LABEL,
  coordinates,
  distance,
  integrityFailures,
  integrityStanding,
  integrityTone,
  metres,
  overrideDistanceM,
  standingOf,
  standingTone,
  type PositionStanding,
} from "../lib/evidence.js";
import { humanise, utcMoment } from "../lib/format.js";

/**
 * The photographic record for one job — PAS 2030:2023 clause 6.11.
 *
 * This is the screen the product is sold on, so it is worth stating plainly
 * what it has to do and what would count as it failing.
 *
 * Every competitor stamps a photograph with a coordinate. The claim here is
 * that the stamp can be proved: the fix is shown with its accuracy rather than
 * on its own, the bytes are re-hashed on demand rather than trusted, and — the
 * one that matters most — a position somebody typed in is never allowed to look
 * like a position a receiver measured.
 *
 * That last one is the failure this screen is built around. An assessor
 * skim-reading forty photographs must not be able to pass over a hand-set
 * coordinate, and neither must a court. So the override state is not a subtle
 * cue: it has its own word, its own shape, its own block above the coordinate,
 * and the measured fix printed underneath it so the two can be compared. There
 * is no branch anywhere below in which an overridden photograph renders as
 * measured — `standingOf` decides that unconditionally, and `evidence.test.tsx`
 * breaks it deliberately to prove the test can fail.
 *
 * The same rule applies to integrity: "not checked yet" is its own state and is
 * never allowed to read as "fine". A photograph nobody has verified is not a
 * verified photograph.
 *
 * And nothing here relies on colour. These pages get printed for site meetings
 * in black and white, and some of the people who read them every day cannot
 * tell the amber row from the red one.
 */

type CheckState =
  | { readonly status: "checking" }
  | { readonly status: "done"; readonly result: PhotoIntegrity }
  | { readonly status: "failed"; readonly error: unknown };

interface EvidenceRecord {
  readonly photos: readonly PhotoEvidence[];
  readonly sessions: readonly CaptureSession[];
  readonly me: Me;
}

/**
 * A fixed order for the five position states, worst first.
 *
 * Fixed rather than taken from the order the photographs happen to arrive in,
 * so the summary table reads the same way every time and "how many were set by
 * hand" is always the first row a reader's eye lands on.
 */
const STANDING_ORDER: readonly PositionStanding[] = [
  "hand-set",
  "absent",
  "carried",
  "degraded",
  "measured",
];

export function JobPhotos({ jobId }: { readonly jobId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<EvidenceRecord>(
    async () => {
      const [photos, sessions, me] = await Promise.all([
        client.jobPhotos(jobId),
        client.jobCaptureSessions(jobId),
        client.me(),
      ]);
      return { photos, sessions, me };
    },
    [client, jobId],
  );

  return (
    <>
      <div className="page-header spread">
        <div>
          <h1>Photographic record</h1>
          <p className="lede">
            PAS 2030:2023 clause 6.11. Every photograph below is shown with the
            fix the receiver actually took, its accuracy, and whether anybody
            afterwards set the position by hand.
          </p>
        </div>
        <a href={href.job(jobId)}>Back to the job</a>
      </div>

      <RequiredPhotosLookup />

      <Async state={state} what="the photographic record" reload={reload}>
        {(record) => <RecordView record={record} reload={reload} />}
      </Async>
    </>
  );
}

/**
 * The clause 6.11 shot list for one measure, looked up before the work starts.
 *
 * Independent of the job above it: `GET /api/measures/:category/required-photos`
 * takes no job and no organisation, so a device could ask it before this job
 * even existed in the system. It is placed above the record for that reason —
 * it is the checklist an installer reads on site, not a fact about this job's
 * photographs — and it needs no permission gate: the route asks nothing beyond
 * being signed in, which every role that can reach this screen already is.
 *
 * PROVENANCE. `standardsRef` names PAS 2030:2023 §6.11, and the general
 * covered-up-work requirement it states — a photographic record of installation
 * work subsequently covered up — is transcribed from the clause. The specific
 * shot lists per measure below that are not: PAS 2030 is a BSI publication this
 * system does not hold, so each shot carries whether it was actually confirmed
 * against a source, and this screen says so rather than presenting a guess as a
 * citation.
 */
function RequiredPhotosLookup(): ReactNode {
  const { client } = usePortal();
  const [category, setCategory] = useState<MeasureCategory | "">("");
  const [state, setState] = useState<
    | { readonly status: "idle" }
    | { readonly status: "loading" }
    | { readonly status: "done"; readonly result: RequiredPhotosForMeasure }
    | { readonly status: "failed"; readonly error: unknown }
  >({ status: "idle" });

  async function look(next: MeasureCategory): Promise<void> {
    setState({ status: "loading" });
    try {
      const result = await client.requiredPhotosForMeasure(next);
      setState({ status: "done", result });
    } catch (error: unknown) {
      setState({ status: "failed", error });
    }
  }

  return (
    <details>
      <summary>What to photograph for a measure — PAS 2030:2023 §6.11</summary>
      <p className="hint small">
        The shot list for one measure category, so it can be read on site
        before the work is covered up rather than discovered missing after:
        once a wall is closed or a board is fixed, a missing photograph cannot
        be taken at any price.
      </p>
      <div className="field">
        <label htmlFor="required-photos-category">Measure</label>
        <select
          id="required-photos-category"
          value={category}
          onChange={(e) => {
            const next = e.target.value as MeasureCategory | "";
            setCategory(next);
            if (next !== "") void look(next);
          }}
        >
          <option value="">Choose a measure…</option>
          {MEASURE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {humanise(c)}
            </option>
          ))}
        </select>
      </div>

      {state.status === "loading" ? (
        <p className="muted small" role="status" aria-live="polite">
          Looking it up…
        </p>
      ) : null}
      {state.status === "failed" ? (
        <Failure
          error={state.error}
          what="the shot list"
          reload={category === "" ? undefined : () => void look(category)}
        />
      ) : null}
      {state.status === "done" ? <RequiredPhotoTable result={state.result} /> : null}
    </details>
  );
}

function RequiredPhotoTable({
  result,
}: {
  readonly result: RequiredPhotosForMeasure;
}): ReactNode {
  if (result.required.length === 0) {
    return (
      <p className="empty">
        No shot list is worked out yet for {humanise(result.measureCategory)}.
        The general clause 6.11 requirement to photograph covered-up work still
        applies; this system just has no measure-specific list for it.
      </p>
    );
  }

  return (
    <div className="table-scroll">
      <table>
        <caption>
          {result.standardsRef} — read before the work starts. After the
          render, the board or the plaster goes on, a missing shot cannot be
          retaken.
        </caption>
        <thead>
          <tr>
            <th scope="col">Shot</th>
            <th scope="col">Stage</th>
            <th scope="col">Minimum</th>
            <th scope="col">Why</th>
            <th scope="col">Source</th>
          </tr>
        </thead>
        <tbody>
          {result.required.map((shot) => (
            <tr key={shot.subject}>
              <th scope="row">{shot.description}</th>
              <td>{STAGE_LABEL[shot.stage]}</td>
              <td>{shot.minimumCount}</td>
              <td className="small">{shot.why}</td>
              <td className="small">
                {shot.confirmed ? (
                  "Confirmed against PAS 2030"
                ) : (
                  <span className="muted">
                    Not yet confirmed against the published standard — treat as
                    a working requirement, not a citation
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecordView({
  record,
  reload,
}: {
  readonly record: EvidenceRecord;
  readonly reload: () => void;
}): ReactNode {
  const { photos, sessions, me } = record;

  if (photos.length === 0) {
    return (
      <>
        <p className="empty" role="status" aria-live="polite">
          No photographs on this job. That is a statement about this record and
          not about the work: photographs are taken in the field app and arrive
          when a device syncs, so an empty record can mean nothing has been
          captured or that nothing has been sent yet.
        </p>
        <Visits sessions={sessions} />
      </>
    );
  }

  return (
    <>
      <Summary photos={photos} />
      <Legend />
      <Visits sessions={sessions} />
      <section>
        <h2>
          The photographs <span className="count">{photos.length}</span>
        </h2>
        {photos.map((photo) => (
          <Photograph key={photo.id} photo={photo} me={me} onAmended={reload} />
        ))}
      </section>
    </>
  );
}

/**
 * What this record amounts to, in one sentence a reader hears before touring it.
 *
 * The count of hand-set positions leads, because it is the number that decides
 * whether the record can be relied on, and burying it under a total would let
 * somebody read "40 photographs" as "40 located photographs".
 */
function Summary({ photos }: { readonly photos: readonly PhotoEvidence[] }): ReactNode {
  const counts = new Map<PositionStanding, number>();
  for (const photo of photos) {
    const standing = standingOf(photo);
    counts.set(standing, (counts.get(standing) ?? 0) + 1);
  }
  const evidential = photos.filter((p) => POSITION_IS_EVIDENCE[standingOf(p)]).length;
  const handSet = counts.get("hand-set") ?? 0;

  return (
    <>
      <p className="lede" role="status" aria-live="polite">
        {photos.length} {photos.length === 1 ? "photograph" : "photographs"}, of
        which {evidential} carry a position the receiver measured to the accuracy
        the capture settings accepted.{" "}
        {handSet === 0
          ? "None had its position set by hand."
          : `${handSet} had ${handSet === 1 ? "its" : "their"} position set by hand and ${
              handSet === 1 ? "is" : "are"
            } not a location record within the meaning of clause 6.11.`}
      </p>
      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">
            How many photographs are in each position state
          </caption>
          <thead>
            <tr>
              <th scope="col">Position</th>
              <th scope="col">Photographs</th>
              <th scope="col">Stands as a clause 6.11 location record</th>
            </tr>
          </thead>
          <tbody>
            {STANDING_ORDER.filter((s) => (counts.get(s) ?? 0) > 0).map((standing) => (
              <tr key={standing}>
                <th scope="row">
                  <StandingWord standing={standing} />
                </th>
                <td>{counts.get(standing)}</td>
                <td>{POSITION_IS_EVIDENCE[standing] ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * The word and the shape together, which is how every position state is
 * rendered on this screen without exception.
 *
 * The mark is `aria-hidden` because a screen reader gets the word; it is there
 * for the photocopied copy and for the reader who cannot tell the colours
 * apart. The class carries the colour and is never the only channel.
 */
function StandingWord({ standing }: { readonly standing: PositionStanding }): ReactNode {
  return (
    <span className={`status-word status-${standingTone(standing)}`}>
      <span aria-hidden="true">{POSITION_MARK[standing]} </span>
      {POSITION_WORD[standing]}
    </span>
  );
}

/** What the five words mean, for somebody meeting this record for the first time. */
function Legend(): ReactNode {
  return (
    <details>
      <summary>What the position words mean</summary>
      <dl className="fields">
        {STANDING_ORDER.map((standing) => (
          <div key={standing}>
            <dt>
              <StandingWord standing={standing} />
            </dt>
            <dd>{POSITION_MEANING[standing]}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/**
 * The visits, and where the operator departed from what the app proposed.
 *
 * A session whose mode differs from the proposed one carries a recorded reason,
 * and that reason is the audit trail for why a LiDAR scan was not taken on a
 * job that expected one. Shown rather than filed away, because "the device
 * could not" and "we did not bother" are different answers.
 */
function Visits({ sessions }: { readonly sessions: readonly CaptureSession[] }): ReactNode {
  if (sessions.length === 0) return null;
  return (
    <section>
      <h2>
        Visits <span className="count">{sessions.length}</span>
      </h2>
      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">
            Capture sessions on this job, most recent first
          </caption>
          <thead>
            <tr>
              <th scope="col">Started</th>
              <th scope="col">What was done</th>
              <th scope="col">Device</th>
              <th scope="col">Ended</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.id}>
                <th scope="row" className="ts">
                  {utcMoment(session.startedAt)}
                </th>
                <td>
                  {humanise(session.mode)}
                  {session.mode === session.proposedMode ? null : (
                    <>
                      <br />
                      <span className="status-word status-approaching">
                        <span aria-hidden="true">◔ </span>
                        Departed from the proposed {humanise(session.proposedMode)}
                      </span>
                      <br />
                      <span className="muted small">
                        {session.overrideReason ??
                          "No reason was recorded, which the capture route does not permit — treat this session as unexplained."}
                      </span>
                    </>
                  )}
                </td>
                <td className="small">
                  {session.device.model}, {session.device.os}
                  <br />
                  <span className="muted">
                    {session.device.lidarSupported
                      ? "LiDAR available"
                      : "no LiDAR on this device"}
                  </span>
                </td>
                <td className="ts">
                  {session.endedAt === null ? (
                    <span className="muted">still open</span>
                  ) : (
                    utcMoment(session.endedAt)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Photograph({
  photo,
  me,
  onAmended,
}: {
  readonly photo: PhotoEvidence;
  readonly me: Me;
  readonly onAmended: () => void;
}): ReactNode {
  const { client } = usePortal();
  const [check, setCheck] = useState<CheckState | null>(null);
  const standing = standingOf(photo);

  const verify = useCallback(() => {
    setCheck({ status: "checking" });
    client.verifyPhoto(photo.id).then(
      (result) => setCheck({ status: "done", result }),
      (error: unknown) => setCheck({ status: "failed", error }),
    );
  }, [client, photo.id]);

  return (
    <article className={standing === "hand-set" ? "room has-assumptions" : "room"}>
      <h3>
        {photo.description ?? photo.capturedDescription ?? humanise(photo.subject)}
      </h3>

      {/* The picture itself, which this screen described in full and never
          showed. A clause 6.11 record is an argument that a particular
          photograph was taken at a particular place and time; a reader who
          cannot see the photograph can only take the surrounding fields on
          trust, which is the one thing an evidence screen must not ask.

          The STAMPED copy, because the stamp is what carries the time and the
          position into the image itself. `object-fit: contain` rather than
          `cover`: a cropped evidence photograph is a different photograph. */}
      <figure className="evidence-photo">
        <PhotoImage photo={photo} copy="stamped" />
      </figure>

      <AmendDescription photo={photo} me={me} onAmended={onAmended} />

      <p>
        <StandingWord standing={standing} />{" "}
        <span className="muted small">{POSITION_MEANING[standing]}</span>
      </p>

      {photo.override === null ? null : <Override photo={photo} />}
      <OverridePosition photo={photo} me={me} onOverridden={onAmended} />

      <dl className="fields">
        <div>
          <dt>Stage</dt>
          <dd>
            {STAGE_LABEL[photo.stage]}
            {photo.stage === "pre-cover" ? (
              <>
                {" "}
                <span className="muted small">
                  The shot that cannot be retaken once the work is covered.
                </span>
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Subject</dt>
          <dd>
            {humanise(photo.subject)}
            {photo.measureCategory === null ? null : (
              <> · {humanise(photo.measureCategory)}</>
            )}
          </dd>
        </div>
        {photo.facade === null ? null : (
          <div>
            <dt>Elevation</dt>
            <dd>{FACADE_LABEL[photo.facade]}</dd>
          </div>
        )}
        <div>
          <dt>Taken</dt>
          <dd className="ts">{utcMoment(photo.capturedAt)}</dd>
        </div>
        <div>
          <dt>{photo.override === null ? "Position" : "Position shown on the stamp"}</dt>
          <dd className="mono">
            {coordinates(photo.latitude, photo.longitude) ?? (
              <span className="muted">no coordinate</span>
            )}
          </dd>
        </div>
      </dl>

      <MeasuredFix photo={photo} />

      <details>
        <summary>The stamp burnt into the photograph</summary>
        <ul className="small">
          {photo.stamp.lines.map((line: StampLine, index) => (
            <li key={`${line.kind}-${index}`}>
              {line.emphasis ? <strong>{line.text}</strong> : line.text}
            </li>
          ))}
        </ul>
        <p className="muted small">
          The API also reports this photograph&apos;s position as{" "}
          <strong>
            {photo.stamp.positionIsMeasured
              ? "measured by the receiver"
              : "not measured by the receiver"}
          </strong>
          . That is the field an assessor is asking about, and the words above
          are read from it rather than worked out again here.
        </p>
      </details>

      <Integrity photo={photo} check={check} verify={verify} />

      <p className="muted small">
        Kept until {photo.retainUntil} · {photo.retentionBasis}
      </p>
    </article>
  );
}

/**
 * Corrects the caption, never the evidence.
 *
 * "Office corrects a caption; it never captures" is how the permission matrix
 * describes this grant, and the form below is built to match it exactly: the
 * only field is the description. The measured fix, the bearing, the
 * timestamps and the bytes themselves are not offered here because the
 * database refuses a change to any of them — this is the one column on the
 * record a mistake can actually be fixed on, and `photo.capturedDescription`
 * stays on screen underneath so a reader can see what the device wrote at the
 * shutter even after somebody has corrected it.
 *
 * Gated on the same matrix the API enforces rather than guessed from the role
 * name. `isAssigned` is passed as `true` unconditionally: this component only
 * ever renders for a photograph this browser already read, and a role holding
 * `photo-evidence` at "own" scope could not have reached that read unless it
 * were assigned to the job — so by the time a card is on screen, the fact the
 * server would check is already established.
 */
function AmendDescription({
  photo,
  me,
  onAmended,
}: {
  readonly photo: PhotoEvidence;
  readonly me: Me;
  readonly onAmended: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(photo.description ?? "");
  const [note, setNote] = useState("");

  const mayAmend = can({
    role: me.role,
    resource: "photo-evidence",
    action: "update",
    isAssigned: true,
  });

  if (!mayAmend) return null;

  if (!editing) {
    return (
      <p className="row-actions">
        <button
          className="link"
          onClick={() => {
            setValue(photo.description ?? "");
            setNote("");
            setEditing(true);
          }}
        >
          Correct the caption
        </button>
      </p>
    );
  }

  async function save(): Promise<void> {
    await submit(async () => {
      await client.amendPhotoDescription(
        photo.id,
        value.trim(),
        note.trim() === "" ? undefined : note.trim(),
      );
      setEditing(false);
      onAmended();
    });
  }

  return (
    <div className="notice pending">
      <div className="field">
        <label htmlFor={`caption-${photo.id}`}>Description</label>
        <input
          id={`caption-${photo.id}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <p className="hint small">
          What the device captioned it at the shutter stays on the record
          underneath — this replaces only what is shown as the caption.
        </p>
      </div>
      <div className="field">
        <label htmlFor={`caption-note-${photo.id}`}>
          Why (optional, kept in the audit trail)
        </label>
        <input
          id={`caption-note-${photo.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <p className="row-actions">
        <button disabled={busy || value.trim() === ""} onClick={() => void save()}>
          {busy ? "Saving…" : "Save the correction"}
        </button>{" "}
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() => setEditing(false)}
        >
          Cancel
        </button>
      </p>
      {error !== null ? (
        <Failure error={error} what="that correction" />
      ) : null}
    </div>
  );
}

/**
 * The override block: the loudest thing on the card, above the coordinate.
 *
 * Above rather than beside, because a reader who has already read a coordinate
 * has already formed a belief about it, and a caveat underneath arrives too
 * late. The measured fix is printed here too — never replaced — so the two can
 * be compared, and the distance between them is computed because "somebody
 * moved it four metres" and "somebody moved it four kilometres" are different
 * facts about the same act.
 */
function Override({ photo }: { readonly photo: PhotoEvidence }): ReactNode {
  const override = photo.override;
  if (override === null) return null;
  const moved = overrideDistanceM(photo);

  return (
    <div className="notice fail" role="alert">
      <p>
        <strong>
          <span aria-hidden="true">{POSITION_MARK["hand-set"]} </span>
          Position set by hand — not measured by the receiver
        </strong>
      </p>
      <p>
        This photograph is not a location record within the meaning of PAS
        2030:2023 clause 6.11. Somebody typed the coordinate in after the fact,
        and it is shown because it was recorded, not because it was observed.
      </p>
      <dl className="fields">
        <div>
          <dt>Reason given</dt>
          <dd>
            {override.reason ?? (
              <span className="muted">
                No reason was recorded, which the override route does not permit
                — treat this position as unexplained.
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Hand-set position</dt>
          <dd className="mono">
            {coordinates(override.latitude, override.longitude) ?? (
              <span className="muted">no coordinate</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Distance from the measured fix</dt>
          <dd>
            {moved === null ? (
              "There is nothing to compare it with: the receiver measured no position at all at this moment."
            ) : (
              <>{distance(moved)} away from where the receiver said it was.</>
            )}
          </dd>
        </div>
        <div>
          <dt>Set at</dt>
          <dd className="ts">
            {override.setAt === null ? (
              <span className="muted">not recorded</span>
            ) : (
              utcMoment(override.setAt)
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}

type OverrideStage =
  | { readonly name: "closed" }
  | { readonly name: "editing" }
  | {
      readonly name: "confirming";
      readonly input: { latitude: number; longitude: number; reason: string };
    };

/**
 * Records a manual position correction — the write side of `Override` above.
 *
 * One-way and permanent, matching the API: `overridePhotoPosition` in
 * `apps/api/src/capture/capture-writer.ts` refuses ever to change or remove an
 * override once recorded, so there is no "edit" or "undo" anywhere in this
 * component, only "record" and a chance to read it back first. That is why
 * this is a two-stage form rather than a single button, on the same pattern
 * `SendPanel` in `Documents.tsx` uses for the other irreversible, outward-facing
 * act this portal offers: nothing is sent to the API until a person has seen
 * exactly what will be recorded and pressed a second, differently-worded
 * button to confirm it.
 *
 * Gated on the same matrix the API enforces, computed exactly as
 * `AmendDescription` computes it above: `isAssigned` is passed as `true`
 * unconditionally, because this component only ever renders for a photograph
 * this browser already read, and an "own"-scoped role could not have reached
 * that read without being assigned to the job.
 *
 * Renders nothing once an override already exists — there is nothing left
 * this form could still do, whatever the role holds — and a plain sentence
 * rather than a control for a role that holds no `update` at all, so a button
 * that could only ever come back refused is never shown.
 */
function OverridePosition({
  photo,
  me,
  onOverridden,
}: {
  readonly photo: PhotoEvidence;
  readonly me: Me;
  readonly onOverridden: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [stage, setStage] = useState<OverrideStage>({ name: "closed" });
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [reason, setReason] = useState("");

  const mayOverride = can({
    role: me.role,
    resource: "photo-evidence",
    action: "update",
    isAssigned: true,
  });

  // Once an override is recorded the API refuses ever to change or remove it,
  // so there is nothing left for this form to do regardless of the role — the
  // `Override` block above is the only thing that still applies.
  if (photo.override !== null) return null;

  if (!mayOverride) {
    return (
      <p className="small muted">
        Your role may not <span className="mono">update</span>{" "}
        <span className="mono">photo-evidence</span>, so a manual position
        correction cannot be recorded here. Ask somebody who holds that grant —
        this is not offered as a control that could only ever come back
        refused.
      </p>
    );
  }

  const lat = latitude.trim() === "" ? null : Number(latitude);
  const lon = longitude.trim() === "" ? null : Number(longitude);
  const latValid = lat !== null && Number.isFinite(lat) && lat >= -90 && lat <= 90;
  const lonValid = lon !== null && Number.isFinite(lon) && lon >= -180 && lon <= 180;
  const reasonValid = reason.trim() !== "";
  const complete = latValid && lonValid && reasonValid;

  function reset(): void {
    setLatitude("");
    setLongitude("");
    setReason("");
    setStage({ name: "closed" });
  }

  if (stage.name === "closed") {
    return (
      <p className="row-actions">
        <button className="link" onClick={() => setStage({ name: "editing" })}>
          Set the position by hand
        </button>
      </p>
    );
  }

  if (stage.name === "confirming") {
    const { input } = stage;
    return (
      <div className="notice fail" role="alert">
        <p>
          <strong>This cannot be undone.</strong> The API records a manual
          position override permanently and refuses ever to change or remove
          one — not from this screen, not from any other. Read back exactly
          what is about to be recorded before you confirm it.
        </p>
        <dl className="fields">
          <div>
            <dt>Position to record</dt>
            <dd className="mono">
              {coordinates(input.latitude, input.longitude)}
            </dd>
          </div>
          <div>
            <dt>Reason</dt>
            <dd>{input.reason}</dd>
          </div>
        </dl>
        <p>
          <button
            disabled={busy}
            onClick={() =>
              void submit(async () => {
                await client.overridePhotoPosition(photo.id, input);
                reset();
                onOverridden();
              })
            }
          >
            {busy ? "Recording…" : "Record this override — permanently"}
          </button>{" "}
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => setStage({ name: "editing" })}
          >
            Go back and change it
          </button>
        </p>
        {error !== null ? <Failure error={error} what="that override" /> : null}
      </div>
    );
  }

  return (
    <div className="notice pending">
      <p>
        Only use this when the device measured no position, or measured the
        wrong one. This is a manual correction, typed in after the fact — it is
        not a re-measurement, the API can never be asked to change or remove
        it, and the record above will say plainly that the position was set by
        hand rather than measured.
      </p>
      <div className="field">
        <label htmlFor={`override-lat-${photo.id}`}>Latitude</label>
        <input
          id={`override-lat-${photo.id}`}
          inputMode="decimal"
          value={latitude}
          aria-invalid={latitude.trim() !== "" && !latValid ? "true" : undefined}
          onChange={(e) => setLatitude(e.target.value)}
        />
        {latitude.trim() !== "" && !latValid ? (
          <p className="hint small fail">
            Latitude must be a finite number between -90 and 90.
          </p>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor={`override-lon-${photo.id}`}>Longitude</label>
        <input
          id={`override-lon-${photo.id}`}
          inputMode="decimal"
          value={longitude}
          aria-invalid={longitude.trim() !== "" && !lonValid ? "true" : undefined}
          onChange={(e) => setLongitude(e.target.value)}
        />
        {longitude.trim() !== "" && !lonValid ? (
          <p className="hint small fail">
            Longitude must be a finite number between -180 and 180.
          </p>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor={`override-reason-${photo.id}`}>
          Why the measured position was not used
        </label>
        <input
          id={`override-reason-${photo.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="hint small">
          Required. The API refuses an override with no reason recorded — a
          coordinate somebody chose with no reason is the artefact this record
          exists to prevent.
        </p>
      </div>
      <p className="row-actions">
        <button
          disabled={!complete}
          onClick={() => {
            if (lat === null || lon === null) return;
            setStage({
              name: "confirming",
              input: { latitude: lat, longitude: lon, reason: reason.trim() },
            });
          }}
        >
          Review this override
        </button>{" "}
        <button type="button" className="secondary" onClick={reset}>
          Cancel
        </button>
      </p>
    </div>
  );
}

/**
 * What the receiver reported, kept separate from what the stamp shows.
 *
 * The accuracy lives here and only here. Printing "±3.2 m" beside a hand-set
 * coordinate would attach a receiver's tolerance to a number a person chose,
 * which is a more convincing lie than the coordinate on its own.
 */
function MeasuredFix({ photo }: { readonly photo: PhotoEvidence }): ReactNode {
  const measured = photo.measured;
  const pair = coordinates(measured.latitude, measured.longitude);

  return (
    <>
      <h4 className="small">What the receiver measured</h4>
      <dl className="fields">
        <div>
          <dt>Fix</dt>
          <dd>
            {pair === null ? (
              <span className="muted">
                The receiver measured no position at all.
              </span>
            ) : (
              <>
                <span className="mono">{pair}</span>{" "}
                {measured.accuracyM === null ? (
                  <span className="muted">
                    with no accuracy recorded, so the coordinate carries no
                    tolerance
                  </span>
                ) : (
                  <strong>{metres(measured.accuracyM)}</strong>
                )}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Measured at</dt>
          <dd className="ts">
            {photo.gpsCapturedAt === null ? (
              <span className="muted">not recorded</span>
            ) : (
              utcMoment(photo.gpsCapturedAt)
            )}
          </dd>
        </div>
        <div>
          <dt>Bearing</dt>
          <dd>
            {measured.bearingDeg === null || measured.bearingRef === null ? (
              <span className="muted">
                No bearing recorded, so which way the camera was pointing is not
                part of this record.
              </span>
            ) : (
              <>
                {measured.bearingDeg.toFixed(1)}° from{" "}
                {measured.bearingRef === "true" ? "true north" : "magnetic north"}
              </>
            )}
          </dd>
        </div>
        {measured.altitudeM === null ? null : (
          <div>
            <dt>Altitude</dt>
            <dd>{measured.altitudeM.toFixed(1)} m</dd>
          </div>
        )}
        {photo.fixQuality === "last-known" ? (
          <div>
            <dt>How stale the carried fix is</dt>
            <dd>
              {measured.lastFixAgeSeconds === null
                ? "Age not recorded."
                : `Taken ${measured.lastFixAgeSeconds} seconds earlier`}
              {measured.lastFixDistanceM === null
                ? ""
                : `, ${distance(measured.lastFixDistanceM)} away.`}
            </dd>
          </div>
        ) : null}
      </dl>
    </>
  );
}

/**
 * Whether the stored bytes are still the bytes that were recorded.
 *
 * Asked on demand rather than on load, because it re-hashes two objects per
 * photograph and a job can carry forty. Until it is asked, the state says "Not
 * checked" in as many words: a screen that showed nothing until the answer came
 * back would let a reader take the silence for a yes.
 *
 * Both halves are reported. The stamped copy is what a person reads and the
 * original is what gets re-examined when somebody disputes what the photograph
 * shows; they can fail apart, and a screen that showed one verdict would prove
 * the caption rather than the evidence.
 */
function Integrity({
  photo,
  check,
  verify,
}: {
  readonly photo: PhotoEvidence;
  readonly check: CheckState | null;
  readonly verify: () => void;
}): ReactNode {
  const result = check !== null && check.status === "done" ? check.result : null;
  const standing = integrityStanding(result);

  return (
    <section>
      <h4 className="small">Integrity of the stored files</h4>
      <p>
        <span className={`status-word status-${integrityTone(standing)}`}>
          <span aria-hidden="true">{INTEGRITY_MARK[standing]} </span>
          {INTEGRITY_WORD[standing]}
        </span>{" "}
        {standing === "unchecked" ? (
          <span className="muted small">
            Nobody has re-hashed these bytes in this browser. That is not the
            same as their being sound.
          </span>
        ) : null}
      </p>

      {result !== null && !result.ok ? (
        <div className="notice fail" role="alert">
          <p>
            <strong>
              This photograph is not the photograph that was stored.
            </strong>{" "}
            Do not rely on it as evidence, and do not send it to anybody, until
            somebody has established what happened to the file.
          </p>
          <ul>
            {integrityFailures(result).map((failure) => (
              <li key={failure}>{failure}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {result !== null && result.ok ? (
        <p className="muted small">
          Both halves of the dual capture re-hashed to the digests recorded when
          the photograph arrived, and each file is still stored under the
          content-addressed name those digests produce.
        </p>
      ) : null}

      {check !== null && check.status === "failed" ? (
        <Failure error={check.error} what="the integrity check" reload={verify} />
      ) : null}

      <p className="row-actions">
        <button
          onClick={verify}
          disabled={check !== null && check.status === "checking"}
        >
          {check === null
            ? "Check the stored files"
            : check.status === "checking"
              ? "Checking…"
              : "Check again"}
        </button>
      </p>

      <dl className="fields small">
        <div>
          <dt>Original recorded as</dt>
          <dd className="mono">{photo.integrity.originalSha256}</dd>
        </div>
        <div>
          <dt>Stamped recorded as</dt>
          <dd className="mono">{photo.integrity.stampedSha256}</dd>
        </div>
      </dl>
    </section>
  );
}
